// src/components/BookNowPublicModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, IconButton, Select, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY; // set this in env

function dollarsFromCents(c) { return `$${(Number(c || 0) / 100).toFixed(2)}`; }
function fmtTime(iso, timeZone) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone });
}


// small popover with click-outside close
function DatePickerPopover({ open, onClose, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose?.();
    }
    function onEsc(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="ab-popover" ref={ref} role="dialog" aria-modal="true">
      {children}
    </div>
  );
}


// --- Small inline calendar that only enables provided availableDates (YYYY-MM-DD) ---
function InlineCalendar({ availableDates = [], value, onChange }) {
  // Normalize available into a Set for O(1) lookups
  const avail = useMemo(() => new Set(availableDates || []), [availableDates]);

  // Start month = first available date, else today
  const firstDate = useMemo(() => {
    if (availableDates?.length) return new Date(availableDates[0] + "T00:00:00");
    return new Date();
  }, [availableDates]);

  const [month, setMonth] = useState(firstDate.getMonth());
  const [year, setYear] = useState(firstDate.getFullYear());

  useEffect(() => {
    setMonth(firstDate.getMonth());
    setYear(firstDate.getFullYear());
  }, [firstDate]);

  const startOfMonth = new Date(year, month, 1);
  const startWeekday = startOfMonth.getDay(); // Sun=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const grid = [];
  // pad leading blanks
  for (let i = 0; i < startWeekday; i++) grid.push(null);
  // actual days
  for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(year, month, d));

  const ym = (n) => (n < 10 ? `0${n}` : `${n}`);
  const ymd = (d) => `${d.getFullYear()}-${ym(d.getMonth() + 1)}-${ym(d.getDate())}`;

  return (
    <div className="ab-cal">
      <div className="ab-cal-top">
        <button className="ab-cal-nav" onClick={() => {
          const m = month - 1;
          if (m < 0) { setMonth(11); setYear(year - 1); }
          else setMonth(m);
        }} aria-label="Prev month">‹</button>
        <div className="ab-cal-title">
          {startOfMonth.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </div>
        <button className="ab-cal-nav" onClick={() => {
          const m = month + 1;
          if (m > 11) { setMonth(0); setYear(year + 1); }
          else setMonth(m);
        }} aria-label="Next month">›</button>
      </div>

      <div className="ab-cal-grid ab-cal-head">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="ab-cal-headcell">{d}</div>)}
      </div>

      <div className="ab-cal-grid">
        {grid.map((d, i) => {
          if (!d) return <div key={`b${i}`} className="ab-cal-cell" />;
          const k = ymd(d);
          const enabled = avail.has(k);
          const isSelected = value === k;
          return (
            <button
              key={k}
              className={`ab-cal-cell ${enabled ? "is-enabled" : "is-disabled"} ${isSelected ? "is-selected" : ""}`}
              onClick={() => enabled && onChange?.(k)}
              disabled={!enabled}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- Card subform using Stripe Elements ---
export function PaymentInputs({ onCardChange, preview = false }) {
  const [cardError, setCardError] = useState("");

  // Preview-only: static Stripe-like row to reuse the same look without needing Elements context
  if (preview) {
    return (
      <div className="pl-bn-narrow" style={{ maxWidth: 360, margin: "0 auto" }}>
        <div
          className="pl-card-wrap"
          style={{
            background: "transparent",
            border: "none",
            borderBottom: "1px solid #83443A",
            borderRadius: 0,
            padding: 0,
            paddingBottom: 4,
            marginTop: 25,
            marginBottom: 10,
            pointerEvents: "none",
          }}
        >
          <div
            className="pl-card-field"
            style={{
              height: 36,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "4px 2px",
              boxSizing: "border-box",
              width: "100%",
            }}
          >
            <span style={{ opacity: 0.75, fontSize: "0.95rem" }} aria-hidden="true">💳</span>
            <span style={{ color: "rgba(131,68,58,0.7)", fontSize: "0.85rem" }}>Card number</span>
            <span style={{ marginLeft: "auto", color: "rgba(131,68,58,0.55)", fontSize: "0.85rem" }}>MM / YY</span>
            <span style={{ color: "rgba(131,68,58,0.55)", fontSize: "0.85rem" }}>CVC</span>
          </div>
        </div>
      </div>
    );
  }

  // CardElement handles number/expiry/cvc securely
  return (
    <div className="pl-bn-narrow">
      <div className="pl-card-wrap">
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                fontSize: "13.6px",
                color: "#83443A",
                fontFamily: "inherit",
                "::placeholder": { color: "rgba(131,68,58,0.7)" },
              },
              invalid: { color: "#B00020" },
            },
          }}
          onChange={(e) => {
            setCardError(e.error?.message || "");
            onCardChange?.(!!e.complete);
          }}
        />
      </div>
      {cardError && <div className="pl-bn-error">{cardError}</div>}
    </div>
  );
}

function ModalInner({
  open, onClose, creatorName, avatarUrl, username, onContinue, hostTimeZone: hostTzProp
}) {
  const activity = open || {};
  const activityId = activity.id || activity._id;
  const title = activity.title || "Activity";
  const location = activity.address || activity.location || "—";
  const description = activity.description;
  const durationLabel = activity.durationLabel || "—";
  const extraCostType = activity.extraCostType === "paid_separately" ? "paid_separately" : "included";
  const extraCostNote = activity.extraCostNote || "";

  // Server-provided availability/pricing
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [error, setError] = useState("");

  // UI state
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStartISO, setSelectedStartISO] = useState("");

  // Customer info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [intro, setIntro] = useState("");

  const [calOpen, setCalOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);


  const prettyDate = useMemo(() => {
    if (!selectedDate) return "";
    const d = new Date(`${selectedDate}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }, [selectedDate]);

  const stripe = useStripe();
  const elements = useElements();

  const slotsForSelectedDate = useMemo(() => {
    if (!availability || !selectedDate) return [];
    const day = (availability.days || []).find((d) => d.date === selectedDate);
    return day ? (day.slots || []) : [];
  }, [availability, selectedDate]);

  const safeTz = (tz) => {
    try {
      if (!tz) throw new Error("missing");
      new Intl.DateTimeFormat("en-US", { timeZone: tz }).format(new Date());
      return tz;
    } catch {
      return "UTC";
    }
  };

  const hostTimeZone = safeTz(
    hostTzProp ||
    activity?.timeZone ||
    availability?.displayTimeZone ||
    availability?.timeZone ||
    "UTC"
  );

  const pricing = availability?.pricing;
  const leadMinutes = 0;
  const hasRequiredFields = firstName && lastName && email && selectedDate && selectedStartISO;
  const disableBook =
    submitting ||
    !pricing ||
    !stripe ||
    !elements ||
    !cardComplete ||
    !hasRequiredFields;

  // Fetch availability
  useEffect(() => {
    let alive = true;
    async function run() {
      if (!open || !username || !activityId) return;
      setLoading(true); setError(""); setAvailability(null);
      setSelectedDate(""); setSelectedStartISO("");

      const from = new Date();
      const to = new Date(); to.setDate(to.getDate() + 60);
      const toYMD = (d, tz) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);

      try {
        const url = `${API_BASE_URL}/api/public/listing/${encodeURIComponent(username)}`
          + `?activityId=${encodeURIComponent(activityId)}`
          + `&from=${toYMD(from, hostTimeZone)}&to=${toYMD(to, hostTimeZone)}&only=availability`
          + `&timeZone=${encodeURIComponent(hostTimeZone)}`
          + `&leadMinutes=${leadMinutes}`;
        const r = await fetch(url);
        if (!r.ok) {
          let detail = "";
          try {
            const errJson = await r.json();
            detail = errJson?.error || "";
          } catch (_) {
            detail = await r.text();
          }
          throw new Error(detail || `Failed to load availability (${r.status})`);
        }
        const data = await r.json();
        if (!alive) return;
        const avail = data.availability || null;
        setAvailability(avail);
        if (avail?.availableDates?.length) setSelectedDate(avail.availableDates[0]);
      } catch (e) {
        if (alive) setError("Could not load availability. Please try again.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [open, username, activityId, hostTimeZone]);

  async function handleBook() {
    setError("");
    if (!pricing || !selectedStartISO || !firstName || !lastName || !email) {
      const missing = [];
      if (!firstName) missing.push("first name");
      if (!lastName) missing.push("last name");
      if (!email) missing.push("email");
      if (!selectedDate || !selectedStartISO) missing.push("date and time");
      setError(`Please complete: ${missing.join(", ")}.`);
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    // Do NOT handle raw card data. Let Stripe Elements create a PaymentMethod or confirm directly.
    const card = elements?.getElement(CardElement);
    if (!stripe || !elements || !card) return;
    setSubmitting(true);
    setError("")

    try {
      // 1) Init
      const initRes = await fetch(`${API_BASE_URL}/api/create/booking/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          activityId,
          startTime: selectedStartISO,
          customer: { firstName, lastName, email, intro },
          timeZone: hostTimeZone,
          leadMinutes
        })
      });
      const initJson = await initRes.json();
      if (!initRes.ok) throw new Error(initJson.error || "Failed to start booking.");

      // 2) Confirm PaymentIntent in browser (places hold for later capture)
      const { paymentIntent, error } = await stripe.confirmCardPayment(initJson.clientSecret, {
        payment_method: {
          card,
          billing_details: { name: `${firstName} ${lastName}`, email }
        }
      });
      if (error) throw error;
      if (!paymentIntent || (paymentIntent.status !== "requires_capture" && paymentIntent.status !== "succeeded")) {
        throw new Error("Card authorization failed.");
      }

      // 3) Finalize booking on server
      const finRes = await fetch(`${API_BASE_URL}/api/create/booking/confirm-setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          activityId,
          startTime: selectedStartISO,
          customer: { firstName, lastName, email, intro },
          paymentIntentId: paymentIntent.id,
          timeZone: hostTimeZone,
          leadMinutes
        })
      });
      const finJson = await finRes.json();
      if (!finRes.ok) throw new Error(finJson.error || "Failed to finalize booking.");

      // 4) Redirect to confirmation page
      window.location.assign(finJson.confirmationUrl);
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <div className="pl-modal pv-skin">
        <div className="pl-modal-header">
          <IconButton onClick={onClose} className="pl-modal-close" aria-label="Close">
            <CloseIcon className="pl-modal-close-icon" />
          </IconButton>
          <h2 className="pl-bn-title">Book an Activity With {creatorName || "Your Buddy"}</h2>
        </div>

        {/* Activity summary */}
        <div className="pl-bn-section pl-bn-activity">
          <div className="pl-bn-avatar-wrap">
            {avatarUrl ? <img className="pl-bn-avatar" src={avatarUrl} alt={`${creatorName} avatar`} /> : <div className="pl-bn-avatar pl-bn-avatar-fallback" aria-hidden>🙂</div>}
          </div>
          <div className="pl-bn-divider" />
          <div className="pl-bn-subtitle pl-bn-activity-info">Activity Info</div>

          <div className="pl-bn-fieldset"><div className="pl-bn-label">Name</div><div className="pl-bn-value">{title}</div></div>
          <div className="pl-bn-fieldset"><div className="pl-bn-label">Description</div><div className="pl-bn-value">{description}</div></div>

          <div className="pl-bn-fieldset"><div className="pl-bn-label">Location</div><div className="pl-bn-value">{location}</div></div>
          <div className="pl-bn-fieldset"><div className="pl-bn-label">Duration</div><div className="pl-bn-value">{durationLabel}</div></div>
        </div>

        {/* Date & Time */}
        <div className="pl-bn-section">
          <div className="pl-bn-divider" />
          <div className="pl-bn-subtitle">Date & Time</div>

          <div className="pl-bn-narrow pl-datetime-row">
            {/* DATE */}
            <div className="pl-bn-grid-field">
              <label className="pl-bn-input-label">Date</label>
              <div className="pl-date-anchor">

                <div
                  className="pl-date-trigger"
                  onClick={() => setCalOpen((v) => !v)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setCalOpen((v) => !v)}
                >
                  <CalendarMonthIcon className="pl-bn-input-icon" />
                  <span className={`pl-date-trigger-text ${selectedDate ? "has-date" : ""}`}>
                    {selectedDate ? prettyDate : "Pick a date"}
                  </span>
                </div>

                {/* calendar popover */}
                <DatePickerPopover open={calOpen} onClose={() => setCalOpen(false)}>
                  <InlineCalendar
                    availableDates={availability?.availableDates || []}
                    value={selectedDate}
                    onChange={(d) => {
                      setSelectedDate(d);
                      setSelectedStartISO("");
                      setCalOpen(false);
                    }}
                  />
                </DatePickerPopover>
              </div>
            </div>

            {/* TIME (MUI Select) */}
            <div className="pl-bn-grid-field">
              <label className="pl-bn-input-label">Time</label>
              <div className="pl-bn-input-wrap">
                <Select
                  className="pl-select"
                  value={selectedStartISO || ""}
                  onChange={(e) => setSelectedStartISO(e.target.value)}
                  displayEmpty
                  fullWidth
                  disabled={loading || !selectedDate || !slotsForSelectedDate.length}
                  IconComponent={ArrowDropDownIcon}
                  renderValue={(val) => {
                    if (!val) {
                      return (!selectedDate
                        ? "Pick a date first"
                        : (loading
                          ? "Loading…"
                          : (slotsForSelectedDate.length ? "Choose a time" : "No times available")));
                    }
                    return fmtTime(val, hostTimeZone);
                  }}
                  MenuProps={{
                    disablePortal: false,
                    keepMounted: true,
                    classes: { paper: "pl-menu-paper" },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                  }}
                >
                  {(!selectedDate || !slotsForSelectedDate.length) ? (
                    <MenuItem value="" disabled>
                      {(!selectedDate && "Pick a date first") ||
                        (loading ? "Loading…" : "No times available")}
                    </MenuItem>
                  ) : (
                    slotsForSelectedDate.map(({ startTime }) => (
                      <MenuItem key={startTime} value={startTime}>
                        {fmtTime(startTime, hostTimeZone)}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </div>
            </div>
          </div>

          {/* Optional note removed for cleaner UX */}

        </div>

        {/* Totals + Extra cost note */}
        <div className="pl-bn-section">
          <div className="pl-bn-divider" />
          <div className="pl-bn-subtitle">Total</div>

          <div className="pl-bn-totals pl-bn-narrow">
            <div className="pl-bn-row"><span>Base Price</span><span>{pricing ? dollarsFromCents(pricing.baseCents) : "—"}</span></div>
            <div className="pl-bn-row"><span>Platform Fee</span><span>{pricing ? dollarsFromCents(pricing.platformFeeCents) : "—"}</span></div>
            <div className="pl-bn-row pl-bn-row-strong">
              <span>Total</span>
              <span>
                {pricing ? dollarsFromCents(pricing.totalCents) : "—"}
                {extraCostType === "paid_separately" ? " + activity cost" : ""}
              </span>
            </div>
          </div>

          <div className="pl-bn-narrow">
            {extraCostType === "included" && (
              <>
                <div className="pl-bn-subtitle">Activity Cost: Included</div>
                <div className="pl-bn-note">The cost of the activity is already included in the total.</div>
              </>
            )}

            {extraCostType === "paid_separately" && (
              <>
                <div className="pl-bn-subtitle">Activity Cost: Paid Separately</div>
                <div className="pl-bn-note">
                  {creatorName?.split(" ")[0] || "They"} left the following note:
                </div>
                <div className="pl-bn-note pl-bn-note-box">
                  {extraCostNote || "Additional on-site cost paid directly at the activity."}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Customer info */}
        <div className="pl-bn-section pl-yourinfo">
          <div className="pl-bn-divider" />
          <div className="pl-bn-subtitle">Your Info</div>
          <p className="pl-bn-note">Provide your information so the host knows who they’re meeting.</p>

          <div className="pl-bn-narrow">
            {/* First + Last Name */}
            <div className="pl-bn-grid pl-bn-grid-2col">
              <input
                className="pl-bn-input pl-bn-input--underline pl-bn-center-ph"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <input
                className="pl-bn-input pl-bn-input--underline pl-bn-center-ph"
                placeholder="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            {/* Email */}
            <input
              className="pl-bn-input pl-bn-input--underline pl-bn-center-ph"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {/* Intro Message */}
            <div className="pl-bn-textarea-wrap">
              <textarea
                className="pl-bn-input pl-bn-textarea pl-bn-textarea-box"
                placeholder="Intro Message (Optional)"
                maxLength={150}
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
              />
              <span className="pl-bn-counter">{intro.length}/150</span>
            </div>
          </div>
        </div>
        {/* Payment */}
        <div className="pl-bn-section pl-yourinfo">
          <div className="pl-bn-divider" />
          <div className="pl-bn-subtitle">Payment</div>
          <div className="pl-bn-note pl-bn-note-stripe">🔒 Payments are processed securely via Stripe.</div>
          <PaymentInputs onCardChange={setCardComplete} />
        </div>
        <div className="pl-bn-actions">
          <div className="pl-bn-note">We place a temporary hold on your card that's only charged if {creatorName} accepts your booking.</div>
          {error && <div className="pl-bn-error">{error}</div>}
          <button
            className={`pl-bn-btn pl-bn-btn-primary ${submitting ? "is-loading" : ""}`}
            disabled={disableBook}
            onClick={handleBook}
          >
            {submitting ? <span className="pl-bn-spinner" aria-label="Booking" /> : "Book Now"}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// Wrapper that provides <Elements> once for the modal
export default function BookNowPublicModal(props) {
  const stripePromise = useRef(loadStripe(STRIPE_PUBLISHABLE_KEY)).current;
  if (!props.open) return null;
  return (
    <Elements stripe={stripePromise}>
      <ModalInner {...props} />
    </Elements>
  );
}
